import React, { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { IPropsOf } from 'types';
import * as Yup from 'yup';
import { ITeamFolderPayload } from '@cincel/client';
import { useContext } from 'react';
import { WLContext } from '@contexts';
import { translations } from 'translations';
import { useTranslation } from 'react-i18next';
import { InputField } from '@design-system/inputs';
import { Modal } from '@design-system/elements';

export interface IFormProps extends IPropsOf<'form'> {
  action: 'patch' | 'post' | null;
  defaultValues: ITeamFolderPayload;
  isOpen: boolean;
  onSubmit: (v: ITeamFolderPayload) => Promise<void>;
  onToggle: (v?: boolean) => void;
}

export const Form: React.FC<IFormProps> = (props): JSX.Element => {
  const { action, defaultValues, isOpen, onSubmit, onToggle, ...rest } = props;

  const { color } = useContext(WLContext);
  const { t } = useTranslation();

  // Define schema inside component to avoid initialization timing issues
  const schema = React.useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(translations.ERRORS.REQUIRED_NAME),
      }),
    [],
  );

  const { control, formState, handleSubmit, reset } = useForm({
    defaultValues: defaultValues ?? { name: '' },
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset(defaultValues ?? { name: '' });
  }, [action, defaultValues, reset]);

  return (
    <form id="team-folders-form" onSubmit={handleSubmit(onSubmit)} {...rest}>
      <Modal
        closeOnSuccess={false}
        formId="team-folders-form"
        handleCancel={reset}
        isDisabled={!formState.isValid || formState.isSubmitting || !formState.isDirty}
        isLoading={formState.isSubmitting}
        showModal={isOpen}
        setShowModal={onToggle}
        title={t(
          action === 'post'
            ? translations.MODALS.NEW_FOLDER
            : translations.MODALS.EDIT_FOLDER,
        )}
        width="md:w-6/12 lg:w-4/12">
        <InputField
          className="mb-4 w-full"
          control={control}
          name="name"
          placeholder={t(translations.INPUTS.FOLDER_NAME)}
          size="md"
          primaryColor={color}
          type="text"
        />
      </Modal>
    </form>
  );
};
